import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { useIsTabletOrMobile } from "@/hooks/use-tablet-mobile";
import { useIsMobile } from "@/hooks/use-mobile";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useSubscription } from "@/hooks/useSubscription";

const WelcomeVideoPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isTabletOrMobile = useIsTabletOrMobile();
  const isMobile = useIsMobile();
  const [isActivating, setIsActivating] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { subscription, loading: subscriptionLoading } = useSubscription();

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!user) {
      navigate("/login");
    }
  }, [user, navigate]);

  // Redirect if user already has an active subscription
  useEffect(() => {
    if (!subscriptionLoading && subscription?.isActive) {
      toast({
        title: 'Welcome back!',
        description: 'You already have an active subscription.'
      });
      navigate('/');
    }
  }, [subscription, subscriptionLoading, navigate, toast]);

  const handleVideoWatched = async () => {
    if (isActivating) return;
    
    setIsActivating(true);
    try {
      const { data, error } = await supabase.functions.invoke('activate-trial', {
        body: { planType: 'Plus' }
      });
      
      if (error) throw error;
      
      // Refresh credits cache
      queryClient.invalidateQueries({ queryKey: ['user-credits'] });
      queryClient.invalidateQueries({ queryKey: ['subscription-status'] });
      
      // Show appropriate message based on response
      if (data.already_active) {
        if (data.subscription_status === 'active') {
          toast({
            title: 'Welcome back!',
            description: 'You have an active subscription. Enjoy exploring!'
          });
        } else {
          toast({
            title: 'Welcome back!',
            description: 'Your trial is already active. Enjoy exploring!'
          });
        }
      } else {
        toast({
          title: 'Welcome to Studibudi!',
          description: 'Your 15-day Plus trial is now active. Enjoy exploring!'
        });
      }
      
      navigate('/');
    } catch (error) {
      console.error('Trial activation failed:', error);
      toast({
        title: 'Something went wrong',
        description: 'Please try again or contact support.',
        variant: 'destructive'
      });
    } finally {
      setIsActivating(false);
    }
  };

  // Show loading state while checking subscription
  if (subscriptionLoading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-app-background">
        <Loader2 className="h-8 w-8 animate-spin text-gray-600" />
      </div>
    );
  }

  if (isTabletOrMobile) {
    return (
      <div className="min-h-screen flex flex-col bg-app-background">
          {/* Top Navigation */}
          <nav className="w-full px-4 h-[58px] flex items-center">
            <div className="flex justify-start">
              <img src="/Logo/sblogo.png" alt="Studibudi Logo" className="h-6 w-auto" />
            </div>
          </nav>

          {/* Main Content */}
          <div className="flex-1 flex items-center justify-center px-4">
            <div className="w-full max-w-2xl">
              <div style={!isMobile ? {
                backdropFilter: 'blur(40px)'
              } : {
                backdropFilter: 'blur(40px)'
              }} className="backdrop-blur-3xl rounded-2xl p-6">
                {/* Welcome Content */}
                <div className="space-y-6">
                  <div className="text-center">
                    <h1 className="text-2xl font-medium text-gray-900 mb-4">Welcome to Studibudi!</h1>
                    <p className="text-gray-600 mb-6">
                      Watch this quick video to get started with your learning journey.
                    </p>
                  </div>

                  {/* Video Container */}
                  <div className="rounded-lg overflow-hidden mb-6" style={{ padding: '56.25% 0 0 0', position: 'relative' }}>
                    <iframe
                      src="https://player.vimeo.com/video/1149570669?badge=0&autopause=0&player_id=0&app_id=58479&autoplay=1"
                      frameBorder="0"
                      allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                      referrerPolicy="strict-origin-when-cross-origin"
                      style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                      title="Studibudi Introduction"
                    />
                  </div>

                  {/* Action Button */}
                  <div className="text-center">
                    <Button
                      onClick={handleVideoWatched}
                      disabled={isActivating}
                      className="w-full h-12 bg-black hover:bg-gray-800 text-white font-medium rounded-2xl border-0 shadow-none"
                    >
                      {isActivating ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Activating your trial...
                        </>
                      ) : (
                        "I watched the video"
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
        </div>
      </div>
    );
  }

  // Desktop layout
  return (
    <div className="min-h-screen flex flex-col bg-app-background">
      {/* Top Navigation */}
      <nav className="w-full px-4 h-[58px] flex items-center">
        <div className="flex justify-start">
          <img src="/Logo/sblogo.png" alt="Studibudi Logo" className="h-6 w-auto" />
        </div>
      </nav>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-[80px]">
        <div className="flex-1 flex items-center justify-center">
          <div style={{
            backdropFilter: 'blur(40px)'
          }} className="w-full backdrop-blur-3xl max-w-3xl rounded-2xl p-8">
            {/* Welcome Content */}
            <div className="space-y-8">
              <div className="text-center">
                <h1 className="text-3xl font-medium text-gray-900 mb-4">Welcome to Studibudi!</h1>
                <p className="text-gray-600 mb-8 text-lg">
                  Watch this quick video to get started with your learning journey.
                </p>
              </div>

              {/* Video Container */}
              <div className="rounded-lg overflow-hidden mb-8" style={{ padding: '56.25% 0 0 0', position: 'relative' }}>
                <iframe
                  src="https://player.vimeo.com/video/1149570669?badge=0&autopause=0&player_id=0&app_id=58479&autoplay=1"
                  frameBorder="0"
                  allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                  title="Studibudi Introduction"
                />
              </div>

              {/* Action Button */}
              <div className="text-center">
                <Button
                  onClick={handleVideoWatched}
                  disabled={isActivating}
                  className="w-full max-w-md h-12 bg-black hover:bg-gray-800 text-white font-medium rounded-2xl border-0 shadow-none"
                >
                  {isActivating ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Activating your trial...
                    </>
                  ) : (
                    "I watched the video"
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WelcomeVideoPage;
